import { t, Trans } from '@lingui/macro';
import { saveAs } from 'file-saver';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { Box, Button, MenuItem, TextField } from '@material-ui/core';
import FormSection from '../../form/FormSection';
import NgpSelect from '../../form/NgpSelect';
import {
    AGENT_SSH_KEY_ID_FIELD,
    AGENT_SSH_PASSPHRASES_FIELD,
    AGENT_SSH_PASSWORD_VALUE_FIELD,
    AGENT_SSH_SELECTION_ENABLED_NGP_KEY_OPTION,
    AGENT_SSH_SELECTION_ENABLED_NGP_OPTION,
    AGENT_SSH_SELECTION_FIELD,
    AGENT_SSH_SELECTION_OPTIONS,
} from './AgentSettingsConstants';
import { AUTO_COMPLETE_DEFAULT, MANUAL_ERROR_TYPE } from '../../../utilities/FormUtilities';
import NgpPasswordField from '../../form/NgpPasswordField';
import { useDetailStyles } from './AgentSettingsStyles';
import NgpFieldHelpTip from '../../form/NgpFieldHelpTip';
import { RowFlexLayout } from '../../layout';
import { HTTP_STATUS_400, useDataService } from '../../../services/ApiDataService';
import NgpHiddenField from '../../form/NgpHiddenField';
import { useServerError } from '../../../services/FormErrorService';

const sshKeysApi = '/ipm/v1/admin/agentSSHKeys';
const sshUsername = 'pulse';
const generateSSHKeyButtonText = t`Generate SSH Key`;

export default function AgentSSHAccess({ tooltip }) {
    const classes = useDetailStyles();
    const { watch, setError, setValue } = useFormContext();
    const sshSelection = watch(AGENT_SSH_SELECTION_FIELD);
    const sshPassphrase = watch(AGENT_SSH_PASSPHRASES_FIELD);
    const serverError = useServerError();
    const dataService = useDataService();

    const handleSSHKeyClick = () => {
        const pemFileType = 'application/x-pem-file';
        const fileName = 'nPointPrivateKey.pem';

        return dataService
            .post(sshKeysApi, { passcode: sshPassphrase })
            .then((response) => {
                setValue(AGENT_SSH_KEY_ID_FIELD, response._id);
                setValue(AGENT_SSH_PASSPHRASES_FIELD, '');
                return saveAs(new Blob([response.privateKey], { type: pemFileType }), fileName, {
                    autoBom: true,
                });
            })
            .catch((errorObject) => {
                if (errorObject.getStatus && errorObject.getStatus() === HTTP_STATUS_400) {
                    setError(AGENT_SSH_PASSPHRASES_FIELD, {
                        type: MANUAL_ERROR_TYPE,
                        message: _.get(errorObject, 'responseBody.message', errorObject.message),
                    });
                    return;
                }

                return serverError(errorObject);
            });
    };

    return (
        <FormSection title={<Trans>nPoint SSH Access </Trans>}>
            <NgpHiddenField fieldName={AGENT_SSH_KEY_ID_FIELD} />
            <NgpSelect
                fieldName={AGENT_SSH_SELECTION_FIELD}
                label={t`Manage nPoint SSH Access`}
                required
                className={classes.agentAccessSelection}
            >
                {_.map(AGENT_SSH_SELECTION_OPTIONS, (value, key) => (
                    <MenuItem key={key} value={key}>
                        {value}
                    </MenuItem>
                ))}
            </NgpSelect>

            {sshSelection === AGENT_SSH_SELECTION_ENABLED_NGP_OPTION && (
                <>
                    <TextField
                        label={<Trans>Username</Trans>}
                        disabled
                        inputProps={{
                            size: 60,
                            autoComplete: AUTO_COMPLETE_DEFAULT,
                        }}
                        required
                        value={sshUsername}
                    />

                    <NgpPasswordField
                        fieldName={AGENT_SSH_PASSWORD_VALUE_FIELD}
                        label={<Trans>Password</Trans>}
                        inputProps={{ size: 60, maxLength: 2083 }}
                        required
                        togglePasswordVisibility
                    />
                </>
            )}

            {sshSelection === AGENT_SSH_SELECTION_ENABLED_NGP_KEY_OPTION && (
                <RowFlexLayout vCenter>
                    <NgpPasswordField
                        fieldName={AGENT_SSH_PASSPHRASES_FIELD}
                        label={<Trans>Passphrase</Trans>}
                        inputProps={{ size: 60, maxLength: 2083 }}
                        togglePasswordVisibility
                    />

                    <Box mt={3} mr={2} ml={3}>
                        <Button
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={handleSSHKeyClick}
                            disabled={_.isEmpty(sshPassphrase)}
                        >
                            {generateSSHKeyButtonText}
                        </Button>
                    </Box>

                    <Box pt={3.5}>
                        <NgpFieldHelpTip helpText={tooltip} />
                    </Box>
                </RowFlexLayout>
            )}
        </FormSection>
    );
}
